import { JWT_CONSTANTS } from '@backend/auth'
import { Company, User } from '@backend/domain'
import { CompanyRepository, UserCredentialsRepository, UserRepository } from '@backend/repository'
import { BadRequestException, ConflictException, Injectable, Logger, NotFoundException } from '@nestjs/common'
import { JwtService } from '@nestjs/jwt'
import argon2 from 'argon2'
import { Transactional } from 'typeorm-transactional'
import { CompanyMapper } from '../mappers'
import { CompanyResponse, CreateTenantWithUserRequest } from '../types'
import { TenantSeedService } from './tenant-seed.service'

@Injectable()
export class TenantService {
	private readonly logger = new Logger(TenantService.name)

	constructor(
		private readonly _jwtService: JwtService,
		private readonly _seedService: TenantSeedService,
		private readonly _userRepository: UserRepository,
		private readonly _companyRepository: CompanyRepository,
		private readonly _userCredentialsRepository: UserCredentialsRepository,
	) {}

	async findById(id: string): Promise<CompanyResponse> {
		const company = await this._companyRepository.findOne({ where: { id } })
		if (!company) {
			throw new NotFoundException('Company not found')
		}

		return CompanyMapper.entityToResponse(company)
	}

	@Transactional()
	async create(request: CreateTenantWithUserRequest): Promise<void> {
		const exists = await this._userRepository.exist({ where: { email: request.user.email } })
		if (exists) {
			throw new ConflictException('A user with same email already exists')
		}

		const email = await this.verifyToken(request.verificationToken)
		if (email !== request.user.email) {
			throw new BadRequestException('Invalid or malformed token provided')
		}

		this.logger.verbose({ message: `Creating tenant ${request.companyName}`, request })

		let company: Partial<Company> = {
			name: request.companyName,
			email: request.user.email,
			emailVerified: true,
		}

		company = await this._companyRepository.save(company)
		// Include tenant id in company also to make tenant filtering work
		await this._companyRepository.update({ id: company.id }, { tenantId: company.id })

		let password: string
		try {
			password = await argon2.hash(request.user.password)
		} catch (error) {
			this.logger.error(error.message)
			throw new BadRequestException()
		}

		const credentials = await this._userCredentialsRepository.save({ tenantId: company.id, password })

		let user: Partial<User> = {
			firstName: request.user.firstName,
			lastName: request.user.lastName,
			email: request.user.email,
			emailVerified: true,
			tenantId: company.id,
			isAdmin: true,
			credentials,
		}
		user = await this._userRepository.save(user)
		this.logger.debug('Saved new tenant', { companyId: company.id, companyName: company.name })

		await this._seedService.seed(company.id as string, user as User, request)
		this.logger.log({ message: 'Tenant was successfully setup', companyId: company.id, companyName: company.name })
	}

	private async verifyToken(token: string): Promise<string> {
		try {
			const email = await this._jwtService
				.verifyAsync(token, { secret: JWT_CONSTANTS.SECRET })
				.then(({ email }) => email as string)
			return email
		} catch {
			throw new BadRequestException('Invalid or malformed token provided')
		}
	}
}
